import { gatsbyDevMode, nodeEnv } from '@ecomm/data-env-variables'
import { addAtomDebugLabel } from '@ecomm/utils/addAtomDebugLabel'
import { useURLSearchParam } from '@ecomm/utils/useURLSearchParams'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useCallback, useMemo } from 'react'
import { match } from 'ts-pattern'

const devmodeAtom = atomWithStorage<boolean | undefined>(
  'disable_dev',
  undefined
)
addAtomDebugLabel(devmodeAtom, 'dev mode - localstorage')

export const useDevMode = (): readonly [boolean, () => void, () => void] => {
  const [isDisabledByStorage, setIsDisabledByStorage] = useAtom(devmodeAtom)
  const env = useMemo(() => nodeEnv(), [])
  const gatsbyDev = useMemo(() => gatsbyDevMode(), [])
  const [isDisabledByParam, setIsDisabledByParam] =
    useURLSearchParam<boolean>('disable_dev')

  const isDevModeEnabled = useMemo(() => {
    return match([
      isDisabledByStorage === true,
      env === 'development',
      gatsbyDev === 'true',
      isDisabledByParam
    ])
      .when(
        ([disabledByStorage]) => disabledByStorage,
        () => false
      )
      .when(
        ([_, __, ___, disabledByParam]) => disabledByParam,
        () => false
      )
      .when(
        ([_, enabledByDevelopmentEnv]) => enabledByDevelopmentEnv,
        () => gatsbyDev !== 'false'
      )
      .when(
        ([_, __, enabledByGatsbyDev]) => enabledByGatsbyDev,
        () => true
      )
      .otherwise(() => false)
  }, [isDisabledByStorage, env, gatsbyDev, isDisabledByParam])

  const disablePermenently = useCallback(() => {
    setIsDisabledByStorage(true)
  }, [setIsDisabledByStorage])

  const disableTemp = useCallback(() => {
    setIsDisabledByParam(true)
  }, [setIsDisabledByParam])

  return [isDevModeEnabled, disablePermenently, disableTemp]
}
